import { useState, useCallback, useEffect, useRef } from "react";
import type { SidebarState } from "../types";

/**
 * Options for configuring the useSidebarState hook.
 */
export interface UseSidebarStateOptions {
  /** Building ID for scoped localStorage persistence */
  buildingId: string;
  /** Default collapsed state if no stored value exists (defaults to false) */
  defaultCollapsed?: boolean;
}

/**
 * Generates the localStorage key for a given building ID.
 * Uses format: nav_collapsed_${buildingId}
 */
const getStorageKey = (buildingId: string): string => `nav_collapsed_${buildingId}`;

/**
 * Safely reads and parses the collapsed state from localStorage.
 * Returns null if the value is missing or invalid.
 */
const readStoredState = (buildingId: string): boolean | null => {
  try {
    const stored = localStorage.getItem(getStorageKey(buildingId));
    if (stored === null) {
      return null;
    }
    const parsed = JSON.parse(stored);
    // Validate that the parsed value is a boolean
    if (typeof parsed !== "boolean") {
      return null;
    }
    return parsed;
  } catch {
    // Invalid JSON or localStorage access error
    return null;
  }
};

/**
 * Safely writes the collapsed state to localStorage.
 */
const writeStoredState = (buildingId: string, collapsed: boolean): void => {
  try {
    localStorage.setItem(getStorageKey(buildingId), JSON.stringify(collapsed));
  } catch {
    // localStorage might be full or unavailable - silently ignore
  }
};

/** Navigation sidebar expanded width matching Tailwind w-60 */
const NAV_EXPANDED_WIDTH = 240;

/**
 * Minimum content area width needed for the folder tree to be visible.
 * The folder tree uses `@[1080px]:block` on its @container (lowered from
 * 1200 so the tree fits typical laptop viewports without zooming out). The
 * container query evaluates the content-box of the @container div, so we
 * must account for every layer between the content div and that content-box:
 *   1080  container query threshold (content-box)
 * +   34  @container div border (2px) + padding p-4 (32px)
 * +   48  page wrapper p-6 left+right padding
 * = 1162  + 2px rounding buffer
 */
const FOLDER_TREE_CONTENT_MIN = 1164;

/**
 * Returns the content width that would be available if the sidebar were expanded,
 * or null if the content element isn't in the DOM (e.g., tests, non-building pages).
 * When null, auto-collapse should be skipped entirely.
 */
const getExpandedContentWidth = (): number | null => {
  const el = document.querySelector<HTMLElement>("[data-sidebar-content]");
  if (!el) return null;
  const paddingRight = parseFloat(getComputedStyle(el).paddingRight) || 0;
  return window.innerWidth - NAV_EXPANDED_WIDTH - paddingRight;
};

/**
 * Custom hook for managing sidebar collapse state with localStorage persistence
 * and responsive auto-collapse behavior.
 *
 * Uses a new storage key format: nav_collapsed_${buildingId}
 * Each building has its own independent collapse state.
 *
 * Auto-collapse: When the viewport is too narrow for the folder tree to be
 * visible with the sidebar expanded, the sidebar collapses automatically.
 * The calculation accounts for the chat sidebar's variable width by reading
 * the content area's actual padding from the DOM.
 *
 * Manual user overrides are respected — if the user explicitly expands the
 * sidebar on a narrow viewport, it stays expanded until the next window
 * resize crosses the threshold.
 *
 * @param options - Configuration options including buildingId
 * @returns SidebarState object with isCollapsed, toggleCollapse, and setCollapsed
 *
 * @example
 * ```tsx
 * const { isCollapsed, toggleCollapse, setCollapsed } = useSidebarState({
 *   buildingId: "123",
 *   defaultCollapsed: false
 * });
 * ```
 */
export function useSidebarState(options: UseSidebarStateOptions): SidebarState {
  const { buildingId, defaultCollapsed = false } = options;

  // Initialize state from localStorage or use default
  const [isCollapsed, setIsCollapsedState] = useState<boolean>(() => {
    const stored = readStoredState(buildingId);
    return stored !== null ? stored : defaultCollapsed;
  });

  // Tracks whether the current collapsed state was set by auto-collapse.
  // When true, widening the viewport will auto-expand the sidebar.
  // When false (manual toggle), auto-expand is suppressed.
  const isAutoCollapsedRef = useRef(false);

  // When true, the sidebar should snap instantly (no CSS transition).
  // Set on auto-collapse so content immediately gets the full width.
  const [skipTransition, setSkipTransition] = useState(false);

  // Persist state changes to localStorage only when NOT auto-collapsed
  useEffect(() => {
    if (!isAutoCollapsedRef.current) {
      writeStoredState(buildingId, isCollapsed);
    }
  }, [buildingId, isCollapsed]);

  // Re-read from localStorage when buildingId changes, respecting viewport constraints
  useEffect(() => {
    const expandedWidth = getExpandedContentWidth();

    if (expandedWidth !== null && expandedWidth < FOLDER_TREE_CONTENT_MIN) {
      // Viewport too narrow for expanded sidebar — auto-collapse (snap, no animation)
      setSkipTransition(true);
      setIsCollapsedState(true);
      isAutoCollapsedRef.current = true;
    } else {
      // Wide enough (or no content element) — use stored preference for this building
      const stored = readStoredState(buildingId);
      setSkipTransition(false);
      setIsCollapsedState(stored !== null ? stored : defaultCollapsed);
      isAutoCollapsedRef.current = false;
    }
  }, [buildingId, defaultCollapsed]);

  // Auto-collapse/expand on window resize
  useEffect(() => {
    const handleResize = () => {
      const expandedWidth = getExpandedContentWidth();
      if (expandedWidth === null) return; // no content element — skip auto-collapse

      if (expandedWidth < FOLDER_TREE_CONTENT_MIN) {
        // Expanded sidebar would hide the folder tree → auto-collapse (snap)
        setSkipTransition(true);
        setIsCollapsedState(true);
        isAutoCollapsedRef.current = true;
      } else if (isAutoCollapsedRef.current) {
        // Wide enough for expanded sidebar → auto-expand (animate)
        setSkipTransition(false);
        setIsCollapsedState(false);
        isAutoCollapsedRef.current = false;
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // initial check on mount
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleCollapse = useCallback(() => {
    isAutoCollapsedRef.current = false;
    setSkipTransition(false);
    setIsCollapsedState((prev) => !prev);
  }, []);

  const setCollapsed = useCallback((collapsed: boolean) => {
    isAutoCollapsedRef.current = false;
    setSkipTransition(false);
    setIsCollapsedState(collapsed);
  }, []);

  return {
    isCollapsed,
    skipTransition,
    toggleCollapse,
    setCollapsed,
  };
}
